// Shelf talker signs: one small printed card per product. The same markup is used for the on-screen preview and for printing.
export interface Talker { headline: string; tagline: string; notes: string[]; pairing: string; awards: string[]; badge: string }
export type TalkerStyle = 'bold' | 'classic' | 'minimal';
export type TalkerSize = 'card3x5' | 'card4x6' | 'half';

export const TALKER_SIZES: Record<TalkerSize, { name: string; w: number; h: number; k: number; hint: string }> = {
  card3x5: { name: 'Small card, 3 × 5 in', w: 3, h: 5, k: 1, hint: 'Cut from card stock. Prints 4 to a sheet when you choose that in the print window.' },
  card4x6: { name: 'Medium card, 4 × 6 in', w: 4, h: 6, k: 1.3, hint: 'Fits a 4 × 6 card holder or photo paper.' },
  half: { name: 'Half sheet, 5.5 × 8.5 in', w: 5.5, h: 8.5, k: 1.8, hint: 'Two to a letter sheet. Good for an endcap or a whole shelf.' },
};
export const TALKER_STYLES: Record<TalkerStyle, string> = { bold: 'Bold (dark with gold)', classic: 'Classic (cream, serif)', minimal: 'Minimal (clean white)' };

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
export const RESPONSIBLY = 'Please enjoy responsibly. Must be 21+.';

export interface TalkerCard { talker: Talker; priceCents: number | null; sizeLabel: string; style: TalkerStyle; size: TalkerSize; showPrice: boolean; showNotice: boolean }

const CSS = `
.tk{box-sizing:border-box;position:relative;overflow:hidden;display:flex;flex-direction:column;justify-content:space-between;text-align:center;font-family:Arial,Helvetica,sans-serif;font-size:calc(10pt * var(--k));line-height:1.25}
.tk *{box-sizing:border-box}
.tk .badge{display:inline-block;font-weight:800;letter-spacing:.12em;font-size:.72em;padding:.25em .8em;border-radius:2em;margin:0 auto}
.tk .hl{font-weight:900;font-size:2.1em;line-height:1.05;margin:.5em 0 .25em;text-wrap:balance}
.tk .tag{font-size:1.1em;margin:0 0 .6em}
.tk ul{list-style:none;margin:.2em 0;padding:0;font-size:1em}
.tk li{margin:.25em 0}
.tk .pair{font-size:.95em;margin-top:.5em;font-style:italic}
.tk .award{font-size:.9em;font-weight:700;margin-top:.5em}
.tk .price{font-weight:900;font-size:3.2em;line-height:1}
.tk .size{font-size:.85em;margin-top:.15em;opacity:.85}
.tk .fine{font-size:.62em;opacity:.7;margin-top:.7em}
.tk.bold{background:#14301f;color:#fff;padding:1.2em}
.tk.bold .badge{background:#e1b04a;color:#14301f}.tk.bold .hl{color:#fff}.tk.bold .price{color:#e1b04a}.tk.bold ul{color:#e8efe9}.tk.bold .rule{border-top:2px solid #e1b04a}
.tk.classic{background:#fbf6e9;color:#2b2118;padding:1.2em;border:.35em double #8a6d2f;font-family:Georgia,'Times New Roman',serif}
.tk.classic .badge{background:#8a6d2f;color:#fff}.tk.classic .hl{color:#5a1f1f}.tk.classic .price{color:#5a1f1f}.tk.classic .rule{border-top:1px solid #8a6d2f}
.tk.minimal{background:#fff;color:#111;padding:1.2em;border:1px solid #111}
.tk.minimal .badge{background:#111;color:#fff}.tk.minimal .hl{color:#111}.tk.minimal .price{color:#111}.tk.minimal .rule{border-top:1px solid #111}
.tk .rule{margin:.6em 2em}
`;

export function talkerInner(c: TalkerCard): string {
  const t = c.talker;
  const price = c.showPrice && c.priceCents != null ? `<div class="price">$${(c.priceCents / 100).toFixed(2)}</div>` : '';
  return `<div class="top">${t.badge ? `<span class="badge">${esc(t.badge)}</span>` : ''}
    <div class="hl">${esc(t.headline)}</div>
    ${t.tagline ? `<p class="tag">${esc(t.tagline)}</p>` : ''}
    ${t.notes.length ? `<ul>${t.notes.map((n) => `<li>${esc(n)}</li>`).join('')}</ul>` : ''}
    ${t.pairing ? `<div class="pair">Pairs with ${esc(t.pairing.replace(/^pairs with\s*/i, ''))}</div>` : ''}
    ${t.awards.map((a) => `<div class="award">★ ${esc(a)}</div>`).join('')}</div>
    <div class="bottom">${price ? '<div class="rule"></div>' : ''}${price}<div class="size">${esc(c.sizeLabel)}</div>${c.showNotice ? `<div class="fine">${RESPONSIBLY}</div>` : ''}</div>`;
}

/** One card as an HTML fragment with its own styles, sized in inches. */
export function talkerCardHtml(c: TalkerCard): string {
  const s = TALKER_SIZES[c.size];
  return `<div class="tk ${c.style}" style="--k:${s.k};width:${s.w}in;height:${s.h}in">${talkerInner(c)}</div>`;
}

export function talkersPageHtml(c: TalkerCard, copies: number): string {
  const s = TALKER_SIZES[c.size];
  const cards = Array.from({ length: copies }, () => `<div class="cell">${talkerCardHtml(c)}</div>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Shelf talkers</title><style>${CSS}
    @page{margin:.25in} body{margin:0;display:flex;flex-wrap:wrap;gap:.1in;align-content:flex-start}
    .cell{break-inside:avoid;page-break-inside:avoid;width:${s.w}in;height:${s.h}in}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style></head><body>${cards}</body></html>`;
}

export function printTalkers(c: TalkerCard, copies: number): boolean {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.open(); w.document.write(talkersPageHtml(c, copies)); w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
  return true;
}

export const TALKER_CSS = CSS;
